import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Loader2, Sparkles, ShieldAlert } from "lucide-react";

/** Admin toggles to enable/disable non-core screens.
 *  Admins always keep access regardless of flag state (see backend `admin_bypass`).
 */
export function FeaturesTab() {
  const [registry, setRegistry] = useState([]);
  const [flags, setFlags] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/public/feature-flags");
      setRegistry(data.registry || []);
      setFlags(data.flags || {});
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (key, next) => {
    setBusy(key);
    // Optimistic — snap back on error.
    setFlags(prev => ({ ...prev, [key]: next }));
    try {
      const { data } = await api.put(`/admin/feature-flags/${key}`, { enabled: next });
      setFlags(prev => ({ ...prev, [key]: data.enabled }));
      toast.success(`${key} · ${data.enabled ? "visible to all users" : "hidden from non-admins"}`);
    } catch (err) {
      setFlags(prev => ({ ...prev, [key]: !next }));
      toast.error(formatApiError(err.response?.data?.detail) || "Toggle failed");
    } finally { setBusy(null); }
  };

  return (
    <div className="space-y-6" data-testid="admin-features-tab">
      <div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#83B68B] mb-1">Screen visibility</div>
        <h2 className="font-display font-black text-3xl tracking-tight">Feature toggles</h2>
        <p className="opacity-60 text-sm mt-2 max-w-2xl leading-relaxed">
          Turn screens on or off for coaches and members. Admins always keep access regardless of these settings
          — great for testing an in-development feature before rolling it out. Turning a feature OFF hides its
          nav link and blocks direct navigation to its route.
        </p>
      </div>

      {loading ? (
        <div className="opacity-60 font-display tracking-widest">LOADING…</div>
      ) : registry.length === 0 ? (
        <div className="card-tactical rounded-md p-10 text-center opacity-60">No toggleable features registered yet.</div>
      ) : (
        <div className="space-y-2" data-testid="admin-features-list">
          {registry.map(feat => {
            const on = !!flags[feat.key];
            return (
              <div key={feat.key} className={`card-tactical rounded-md p-4 flex items-start gap-4 transition-colors ${on ? "" : "opacity-70"}`} data-testid={`feature-row-${feat.key}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display font-bold text-lg tracking-tight">{feat.label}</span>
                    {feat.in_development && (
                      <span className="text-[10px] tracking-widest uppercase px-2 py-0.5 rounded border border-amber-400/50 text-amber-300 bg-amber-500/10 inline-flex items-center gap-1">
                        <Sparkles className="w-2.5 h-2.5" /> Beta
                      </span>
                    )}
                    <code className="text-[10px] opacity-50">{feat.path}</code>
                  </div>
                  <div className="text-xs opacity-70 mt-1 leading-relaxed">{feat.description}</div>
                  {!on && (
                    <div className="mt-2 text-[10px] tracking-widest uppercase text-amber-300 inline-flex items-center gap-1">
                      <ShieldAlert className="w-3 h-3" /> Hidden from coaches + members. Admins can still access.
                    </div>
                  )}
                </div>
                <div className="flex-shrink-0 flex items-center gap-2">
                  {busy === feat.key && <Loader2 className="w-3.5 h-3.5 animate-spin opacity-70" />}
                  <Switch
                    checked={on}
                    disabled={busy === feat.key}
                    onCheckedChange={(v) => toggle(feat.key, v)}
                    data-testid={`feature-toggle-${feat.key}`}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
